import { BrowserRouter as Router, Routes, Route, BrowserRouter, useLocation, useNavigate } from 'react-router-dom'
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import { CartProvider } from './contexts/CartContext'
import { PrescriptionProvider } from './contexts/PrescriptionContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { lazy, Suspense, useEffect, useLayoutEffect, useState } from 'react'
import { CircularProgress, Box } from '@mui/material'
import ErrorBoundary from './components/ErrorBoundary'
import ProtectedRoute from './components/ProtectedRoute'
import Pharmacy from './views/pharmacy';
import ListProduct from 'pages/ListProduct'
import Layout from 'components/Layout'
import SpecialistList from './components/Partner/SpecialistList';
import CoachsList from './components/BeautyCoaching/CoachsList';
import DoctorBooking from './components/Partner/DoctorBooking';
import Payment from './components/Partner/Payment';
import ProductManagment from './components/PartnerB2B/ProductManagment';
import Header from './components/Header';
import ModelTesting from 'pages/ModelTestingAdmin/ModelTestingAdmin'
import ProfileSetting from './components/PartnerB2B/ProfileSettings'
import HealixBot from 'components/Healix/HealixBot'
// import PartnersList from './components/SuperAdmin/Partners/PartnerList';
import ModalTestingPage from 'pages/ModelTestingDoctor/ModalTestingPage/ModalTestingPage'



// AuthGate validates tokens on initialization and renders children only after it's done
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { validateAndRefreshToken, isAuthenticated, hasValidTokens } = useAuth();
  const [isValidating, setIsValidating] = useState(false);
  const [hasValidated, setHasValidated] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (hasValidated) return;
      if (isAuthenticated || hasValidTokens()) {
        setIsValidating(true);
        try {
          await validateAndRefreshToken();
        } catch (error) {
          console.error('Token validation failed:', error);
        } finally {
          setIsValidating(false);
          setHasValidated(true);
        }
      } else {
        setHasValidated(true);
      }
    };
    const timer = setTimeout(validateToken, 100);
    return () => clearTimeout(timer);
  }, [validateAndRefreshToken, isAuthenticated, hasValidTokens, hasValidated]);

  if (isValidating || !hasValidated) {
    // Disabled global waiting screen overlay (keep commented for potential re-enable)
    // return (
    //   <Box
    //     position="fixed"
    //     top={0}
    //     left={0}
    //     right={0}
    //     bottom={0}
    //     display="flex"
    //     justifyContent="center"
    //     alignItems="center"
    //     sx={{ background: 'rgba(122, 167, 190, 0.8)', zIndex: 9999 }}
    //   >
    //     <CircularProgress sx={{ color: 'white' }} />
    //   </Box>
    // );
    return null;
  }

  return <>{children}</>;
};

// Lazy load page components
const AdminPage = lazy(() => import('./pages/AdminPage'))
const SurveyPage = lazy(() => import('./pages/SurveyPage'))
const MultiTreeSurveyPage = lazy(() => import('./pages/MultiTreeSurveyPage'))
const HealixMultiTreeSurveyPage = lazy(() => import('./pages/HealixMultiTreeSurveyPage'))
const LandingPage = lazy(() => import('./pages/LandingPage/LandingPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const SignInPage = lazy(() => import('./pages/SignInPage/SignInPage'))
const SignInPageTester = lazy(() => import('./pages/ModelTestingDoctor/SignInTesterPage/SignInTesterPage'))
const SignUpPage = lazy(() => import('./pages/SignUpPage/SignUpPage'))
//const AboutUsPage = lazy(() => import('./pages/AboutUsPage/AboutUs'))
const ForgetPasswordPage = lazy(() => import('./pages/ForgetPasswordPage/ForgetPassword'))
const ProfessionalInterface = lazy(() => import('./pages/ProfessionalPage/ProfessionalInterface'))
const TeleconsultationPage = lazy(() => import('./pages/TeleconsulationPage/TeleconsultationPage'))
const WelcomePage = lazy(() => import('./pages/WelcomePage/welcome'))
const PartnerInterface = lazy(() => import('./pages/PartnerPage/PartnerInterface'))
const HomePage = lazy(() => import('./pages/NewWebsite/HomePage'))
const HealixHomePage = lazy(() => import('./pages/NewWebsite/HealixHomePage'))
const AboutUs = lazy(() => import('./pages/NewWebsite/AboutUspage'))
const Blogs = lazy(() => import('./pages/NewWebsite/BlogsPage'))
const Contact = lazy(() => import('./pages/NewWebsite/ContactUspage'))
const BlogDetails = lazy(() => import('./pages/NewWebsite/BlogDetailspage'))
const Partner = lazy(() => import('./pages/NewWebsite/Partnerpage'))
const PartnerForm = lazy(() => import('./pages/NewWebsite/PartnerFormpage'))
const ClientProfile = lazy(() => import('./pages/ClientPage/ClientProfile'))
const StartYourJourney = lazy(() => import('./pages/NewWebsite/StartYourJourney'))
const BeautyCoachingPage = lazy(() => import('./pages/BeautyCoachingPage/BeautyCoachingPage'))
const Healix = lazy(() => import('./components/Healix/Healix'))
// Loading component for Suspense
// alaa SECOND LOAING SCREEN IN THE WHOLE APP
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    // bgcolor={(theme) => theme.palette.background.default}
    sx={{
      //background: 'linear-gradient(to bottom, #34165C, #77479C)',
      background: '#F4F2EE',
    }}
  >
    {/* <CircularProgress color="primary" /> */}
    <CircularProgress sx={{ color: 'white' }} />
  </Box>
)

// Create base theme
const baseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0097B2',
      light: '#33AEC6',
      dark: '#006B7F',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2B7C85',
      light: '#4A959E',
      dark: '#1E5960',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FFFFFF',
      paper: '#F8FAFC',
    },
    text: {
      primary: '#1E293B',
      secondary: '#64748B',
    },
    error: {
      main: '#EF4444',
    },
    success: {
      main: '#22C55E',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: '2.5rem', // Smaller base size for mobile
      lineHeight: 1.2,
      '@media (min-width:600px)': {
        fontSize: '3rem',
      },
      '@media (min-width:960px)': {
        fontSize: '3.5rem',
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem', // Smaller base size for mobile
      lineHeight: 1.3,
      '@media (min-width:600px)': {
        fontSize: '2.5rem',
      },
      '@media (min-width:960px)': {
        fontSize: '2.75rem',
      },
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem', // Smaller base size for mobile
      lineHeight: 1.4,
      '@media (min-width:600px)': {
        fontSize: '2rem',
      },
      '@media (min-width:960px)': {
        fontSize: '2.25rem',
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem', // Smaller base size for mobile
      lineHeight: 1.4,
      '@media (min-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.1rem', // Smaller base size for mobile
      lineHeight: 1.5,
      '@media (min-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.9rem', // Smaller base size for mobile
      lineHeight: 1.5,
      '@media (min-width:600px)': {
        fontSize: '1rem',
      },
    },
    body1: {
      fontSize: '0.9rem', // Smaller base size for mobile
      lineHeight: 1.7,
      '@media (min-width:600px)': {
        fontSize: '1rem',
      },
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    // Ensure all CircularProgress use the requested brand color
    MuiCircularProgress: {
      styleOverrides: {
        circle: {
          color: '#131B5C',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: '8px 16px',
          '@media (min-width:600px)': {
            padding: '10px 24px',
          },
          transition: 'all 0.2s ease-in-out',
          fontWeight: 600,
        },
        contained: {
          boxShadow: 'none',
          background: 'linear-gradient(135deg, #0097B2 0%, #006B7F 100%)',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0, 151, 178, 0.2)',
            transform: 'translateY(-2px)',
            background: 'linear-gradient(135deg, #006B7F 0%, #005666 100%)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: '#0097B2',
            },
            '&.Mui-focused': {
              boxShadow: '0px 0px 0px 4px rgba(0, 151, 178, 0.1)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 151, 178, 0.1)',
        },
      },
    },
    // Add responsive container settings
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '16px',
          paddingRight: '16px',
          '@media (min-width:600px)': {
            paddingLeft: '24px',
            paddingRight: '24px',
          },
        },
      },
    },
    // Make dialog more mobile-friendly
    MuiDialog: {
      styleOverrides: {
        paper: {
          margin: '16px',
          width: 'calc(100% - 32px)',
          maxWidth: '100%',
          '@media (min-width:600px)': {
            margin: '32px',
            width: 'auto',
            // maxWidth: '600px',
            maxWidth: '90%', // alaa 
          },
          '@media (min-width:960px)': {
            // maxWidth: '800px',
            maxWidth: '90%', // alaa 
          },
        },
      },
    },
  },
});

// Apply responsive font sizes
const theme = responsiveFontSizes(baseTheme);

// PaymentStatusHandler: checks for payment result in URL and sets a flag in localStorage
// function PaymentStatusHandler() {
//   // useEffect(() => {
//   //   const params = new URLSearchParams(window.location.search);
//   //   const payment = params.get('payment');
//   //   const sessionId = params.get('session_id');
//   //   console.log('App.tsx PaymentStatusHandler running. URL:', window.location.href, 'payment:', payment, 'sessionId:', sessionId);
//   //   if (payment === 'success' && sessionId) {
//   //     localStorage.setItem('stripePaymentResult', 'success');
//   //     localStorage.setItem('stripeSessionId', sessionId);
//   //     console.log('App.tsx PaymentStatusHandler: Set success flags in localStorage.');
//   //   } else if (payment === 'failure') {
//   //     localStorage.setItem('stripePaymentResult', 'failure');
//   //     localStorage.removeItem('stripeSessionId');
//   //     console.log('App.tsx PaymentStatusHandler: Set failure flag in localStorage.');
//   //   } else {
//   //     localStorage.removeItem('stripePaymentResult');
//   //     localStorage.removeItem('stripeSessionId');
//   //     console.log('App.tsx PaymentStatusHandler: Cleared payment flags from localStorage.');
//   //   }
//   // }, [window.location.search]);
//   return null;
// }

// App component
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AuthGate>
          <PrescriptionProvider>
            <CartProvider>
              <ErrorBoundary>
                <Router basename="/">
                  {/* Always scroll to top on route change */}
                  <ScrollToTop />
                  {/* <PaymentStatusHandler /> */}
                  {/* Suspense fallback disabled to remove waiting screen */}
                  <Suspense fallback={null}>
                    <Routes>
                    {/* Pages with Header/Footer layout - public group */}
                    <Route element={<Layout />}>
                      <Route path="/skinDiagnosis" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <LandingPage />
                        </Suspense>
                      } />
                       <Route path="/healix-multi-tree-survey" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <HealixMultiTreeSurveyPage />
                          </ErrorBoundary>
                        </Suspense>
                      
                    } />
                     <Route path="/" element={
                      /* Suspense fallback disabled */
                      <Suspense fallback={null}>
                        <ErrorBoundary>
                          <HomePage/>
                        </ErrorBoundary>
                      </Suspense>
                    } />
                      <Route path="/EPharmacy" element={<Pharmacy />} />
                      <Route path="/healixbot" element={<HealixBot/>} />
                      <Route path="/survey" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <SurveyPage />
                          </ErrorBoundary>
                        </Suspense>
                      } />
                      
                      <Route path="/about-us" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <AboutUs />
                          </ErrorBoundary>
                        </Suspense>
                      } />
                      <Route path="/home" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <HomePage  />
                          </ErrorBoundary>
                        </Suspense>
                      } />
                        <Route path="/healixhome" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <HealixHomePage  />
                          </ErrorBoundary>
                        </Suspense>
                      } />
                      <Route path="/start-your-journey" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <StartYourJourney />
                          </ErrorBoundary>
                        </Suspense>
                      } />
                      <Route path="/healix" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <Healix />
                          </ErrorBoundary>
                        </Suspense>
                      } />
                      <Route path="/specialists" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <SpecialistList />
                          </ErrorBoundary>
                        </Suspense>
                      } />
                      <Route path="/coachs" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <CoachsList />
                          </ErrorBoundary>
                        </Suspense>
                      } />
                      <Route path="/" element={
                      /* Suspense fallback disabled */
                      <Suspense fallback={null}>
                        <ErrorBoundary>
                          <HomePage />
                        </ErrorBoundary>
                      </Suspense>
                    } />
                    <Route path="/blogs" element={
                      /* Suspense fallback disabled */
                      <Suspense fallback={null}>
                        <ErrorBoundary>
                          <Blogs />
                        </ErrorBoundary>
                      </Suspense>
                    } />
                    <Route path="/blog/:id" element={
                      /* Suspense fallback disabled */
                      <Suspense fallback={null}>
                        <ErrorBoundary>
                          <BlogDetails />
                        </ErrorBoundary>
                      </Suspense>
                    } />
                      <Route path="/teleconsultation" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <TeleconsultationPage />
                          </ErrorBoundary>
                        </Suspense>
                      } />
                      <Route element={<ListProduct />} path="/listProduct/:category" />
                      <Route element={<ListProduct />} path="/listproduct" />
                      <Route element={<ListProduct />} path="/listproduct/:category" />
                    </Route>

                    {/* Pages with Header/Footer layout - client protected */}
                    <Route element={
                      <ProtectedRoute allowedRoles={["client"]}>
                        <Layout />
                      </ProtectedRoute>
                    }>
                      <Route path="/book-doctor/:id" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <DoctorBooking />
                          </ErrorBoundary>
                        </Suspense>
                      } />
                      <Route path="/client-profile" element={
                        /* Suspense fallback disabled */
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <ClientProfile />
                          </ErrorBoundary>
                        </Suspense>
                      } />
                    </Route>

                    {/* Pages with Header/Footer layout - public group */}

                    {/* /Professional and /partner with header only (no footer) */}
                    <Route path="/Professional" element={
                      <ProtectedRoute allowedRoles={["professional", "professionnel"]}>
                        <>
                          <Header />
                          {/* Suspense fallback disabled */}
                          <Suspense fallback={null}>
                            <ErrorBoundary>
                              <ProfessionalInterface />
                            </ErrorBoundary>
                          </Suspense>
                        </>
                      </ProtectedRoute>
                    } />
                    <Route path="/partner" element={
                      <ProtectedRoute allowedRoles={["partner", "partenaire"]}>
                        <>
                          <Header />
                          {/* Suspense fallback disabled */}
                          <Suspense fallback={null}>
                            <ErrorBoundary>
                              <PartnerInterface />
                            </ErrorBoundary>
                          </Suspense>
                        </>
                      </ProtectedRoute>
                    } />

                    {/* Pages without Header/Footer layout */}
                    <Route path="/login" element={
                      /* Suspense fallback disabled */
                      <Suspense fallback={null}>
                        <ErrorBoundary>
                          <LoginPage />
                        </ErrorBoundary>
                      </Suspense>
                    } />

                    {/* <Route path="/home" element={<HomePage />} /> */}

                    


                    <Route path="/signIn" element={
                      /* Suspense fallback disabled */
                      <Suspense fallback={null}>
                        <ErrorBoundary>
                          <SignInPage />
                        </ErrorBoundary>
                      </Suspense>
                    } />

                    <Route path="/signInTester" element={
                      /* Suspense fallback disabled */
                      <Suspense fallback={null}>
                        <ErrorBoundary>
                          <SignInPageTester />
                        </ErrorBoundary>
                      </Suspense>
                    } />

                    <Route path="/modelTesting" element={
                      <ProtectedRoute allowedRoles={["doctorTester"]}>
                        <ModalTestingPage />
                      </ProtectedRoute>
                    } />

                    <Route path="/signUp-hidden" element={
                      /* Suspense fallback disabled */
                      <Suspense fallback={null}>
                        <ErrorBoundary>
                          <SignUpPage />
                        </ErrorBoundary>
                      </Suspense>
                    } />
                    <Route path="/forgetPassword" element={
                      /* Suspense fallback disabled */
                      <Suspense fallback={null}>
                        <ErrorBoundary>
                          <ForgetPasswordPage />
                        </ErrorBoundary>
                      </Suspense>
                    } />
                    <Route path="/admin" element={
                      /* Suspense fallback disabled */
                      <Suspense fallback={null}>
                        <ErrorBoundary>
                          <ProtectedRoute allowedRoles={["admin"]}>
                            <AdminPage />
                          </ProtectedRoute>
                        </ErrorBoundary>
                      </Suspense>
                    } />
                    {/* Add survey routes accessible by admin */}
                    <Route path="/survey" element={
                      <ProtectedRoute allowedRoles={["admin", "client"]}>
                        {/* Suspense fallback disabled */}
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <SurveyPage />
                          </ErrorBoundary>
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="/multi-tree-survey" element={
                      <ProtectedRoute allowedRoles={["admin", "client"]}>
                        {/* Suspense fallback disabled */}
                        <Suspense fallback={null}>
                          <ErrorBoundary>
                            <MultiTreeSurveyPage />
                          </ErrorBoundary>
                        </Suspense>
                      </ProtectedRoute>
                    } />

                   
                    <Route path="/partner/payment" element={
                      <ProtectedRoute allowedRoles={["client"]}>
                        <Payment />
                      </ProtectedRoute>
                    } />
                    <Route path="/payment" element={
                      /* Suspense fallback disabled */
                      <Suspense fallback={null}>
                        <ErrorBoundary>
                          <Payment />
                        </ErrorBoundary>
                      </Suspense>
                    } />
                    <Route path="/welcome/:email" element={
                      /* Suspense fallback disabled */
                      <Suspense fallback={null}>
                        <ErrorBoundary>
                          <WelcomePage />
                        </ErrorBoundary>
                      </Suspense>
                    } />




                    {/* <Route path="/test-product-management" element={<ProductManagment />} /> */}



                    <Route path="/contact-us" element={<Contact />} />

                    {/* <Route path="/about-us" element={<AboutUs />} /> */}

                    <Route path="/partnerpage" element={<Partner />} />
                    <Route path="/partner-form" element={<PartnerForm />} />

                    {/* <Route path="/profilepartner" element={<ProfileSettings/>} /> */}



                    {/* Protected Routes */}
                    <Route path="/model-testing" element={
                      <ProtectedRoute allowedRoles={["doctorTester"]}>
                        <Suspense fallback={<LoadingFallback />}>
                          <ErrorBoundary>
                            <ModelTesting />
                          </ErrorBoundary>
                        </Suspense>
                      </ProtectedRoute>
                    } />

                    
                   


                    
   

    

        

                    </Routes>
                  </Suspense>
                </Router>
              </ErrorBoundary>
            </CartProvider>
          </PrescriptionProvider>
        </AuthGate>
      </AuthProvider>
    </ThemeProvider >
  )
}

export default App

// Scroll to top on route changes
function ScrollToTop() {
  const location = useLocation();
  // Ensure browser doesn't restore previous scroll positions
  useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  // Scroll synchronously before paint for new routes
  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.search, location.hash]);
  return null;
}